import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import {
  ContentCopy,
  Download,
  Delete,
  CloudUpload,
  AutoFixHigh,
  Tune,
  Circle,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { checkCompression, checkExpansion, checkPhrasing } from "../../services/englishCheckers";
import Loader  from "../../utils/loader";
import { connect } from "react-redux";
import adminServices from "../../services/adminServices";
import { useSelector } from "react-redux";
import { addUserLogs } from "../../services/userLogServices";
import { useCheckFeatureAccess } from "../CheckFeature";
import SubscriptionRequired from "../SubscriptionRequired";
import { useCheckSubscriptionPlanAccess } from "../SubscriptionAccess";
import { checkAIRequestLimit } from "../aiRequestLimit";

function NewParaphrase({ features, trial = false }) {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const userDetails = useSelector((state) => state.user);
  const userID = userDetails?.currentUser?._id || "";
  const [characters, setCharacters] = useState({ max: 1500, now: 0 });
  const [loading, setLoading] = useState(false);
  const [isParaphrase, setIsParaphrase] = useState(false);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [customText, setCustomText] = useState("Custom");
  const [customTextValue, setCustomTextValue] = useState("");
  const [selectedTone, setSelectedTone] = useState("standard");
  const [selectDropdownTone, setSelectDropdownTone] = useState("select");
  const [windowWidth, setWindowWidth] = useState(0);
  const [isClient, setIsClient] = useState(false);

  const accessToUpload = useCheckFeatureAccess("paraphraser", "upload");
  const accessToParaphrase = useCheckFeatureAccess("paraphraser", "paraphrase");
  const accessToDelete = useCheckFeatureAccess("paraphraser", "delete");
  const accessToParaphraserScreen =
    useCheckSubscriptionPlanAccess("Paraphraser");
  const accessTocustom = useCheckSubscriptionPlanAccess("custom")
  const [subReqOpen, setSubReqOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
  });

  const accessToTone = useCheckSubscriptionPlanAccess("tone")
  const accessToMoreOptions = useCheckSubscriptionPlanAccess("moreOptions")


  const fileInputRef = useRef(null);

  const buttons = [
    { id: "standard", label: "Standard", icon: "📝" },
    { id: "formal", label: "Formal", icon: "🎩" },
    { id: "casual", label: "Casual", icon: "😊" },
    { id: "positive", label: "Positive", icon: "✨" },
    { id: "confident", label: "Confident", icon: "💪" },
    { id: "friendly", label: "Friendly", icon: "🤝" },
    { id: "simple", label: "Simple", icon: "🔹" },
  ];

  const customSuggestions = [
    { label: "Like a Story", icon: "📖" },
    { label: "Like a Poem", icon: "✍️" },
    { label: "Like a CEO", icon: "💼" },
    { label: "Like a Doctor", icon: "🩺" },
    { label: "Like a News", icon: "📰" },
    { label: "Like a Sales Executive", icon: "📊" },
    { label: "Like a Letter", icon: "✉️" },
  ];
  useEffect(() => {
    setIsClient(true);
    setWindowWidth(window.innerWidth);

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (
      !inputText.trim() &&
      (selectDropdownTone === "shorten" || selectDropdownTone === "expand")
    ) {
      setSelectDropdownTone("select");
    }
  }, [inputText, selectDropdownTone]);

  useEffect(() => {
    if (accessToParaphraserScreen === false) {
      setSubReqOpen(true);
    }
  }, [accessToParaphraserScreen]);

  useEffect(() => {
    if (isClient) {
      getParaphraseLimit();
    }
  }, [isClient]);

  const getParaphraseLimit = async () => {
    try {
      let data = await adminServices.getParaphraseLimit();
      setCharacters({ max: data.paraphrase_limit, now: 0 });
    } catch (error) {
      console.error("Error getting paraphrase limit:", error);
      toast.error("Failed to load paraphrase limits");
    }
  };

  const handleInputChange = (event) => {
    const text = event.target.value;
    const length = text.length;

    if (length > characters.max) {
      toast.warn(
        `Character limit exceeded. Maximum ${characters.max} characters allowed.`
      );
      return;
    }

    setInputText(text);
    setCharacters({ ...characters, now: length });

    if (isParaphrase) {
      setIsParaphrase(false);
      setOutputText("");
    }
  };

  const GrammerCheckForParaphrase = async (type) => {
    setLoading(true);

    try {
      if (!inputText.trim()) {
        toast.warning("Please input some text!");
        setLoading(false);
        return;
      }

      let data = {
        text: inputText,
        userId: userID,
        isCoAuthor: false,
      };

      if (type && type !== "defaultPrompt") {
        if (
          [
            "Like a story",
            "Like a poem",
            "Like a letter",
            "Like a sales executive",
            "Like a news",
            "Like a Doctor",
            "Like a CEO",
          ].includes(type)
        ) {
          data.tone = "custom";
          data.customTone = type;
        } else {
          data.tone = type;
        }
      }

      const paraphraseResponse = await checkPhrasing(data);

      if (paraphraseResponse && paraphraseResponse.data) {
        let outputText = "";

        if (Array.isArray(paraphraseResponse.data.output)) {
          outputText = paraphraseResponse.data.output.join("\n");
        } else if (typeof paraphraseResponse.data.output === "string") {
          outputText = paraphraseResponse.data.output;
        } else if (paraphraseResponse.data.paraphrasedText) {
          outputText = paraphraseResponse.data.paraphrasedText;
        } else if (paraphraseResponse.data.result) {
          outputText = paraphraseResponse.data.result;
        } else {
          outputText = JSON.stringify(paraphraseResponse.data);
        }

        if (outputText.startsWith("\n")) {
          outputText = outputText.substring(1);
        }

        setOutputText(outputText);
        setIsParaphrase(true);

        try {
          const logData = {
            userId: sessionStorage.getItem("userId"),
            organizationId: sessionStorage.getItem("organizationId"),
            actionType: "paraphrase",
          };
          await addUserLogs(logData);
        } catch (logError) {
          console.error("Error logging user action:", logError);
        }

        toast.success("Text paraphrased successfully!");
      } else {
        toast.error("No valid response received from the server");
      }
    } catch (error) {
      console.error("Error in GrammerCheckForParaphrase:", error);
      toast.error("Failed to paraphrase text. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const checkWordsCompression = async () => {
    setLoading(true);
    try {
      if (!inputText.trim()) {
        toast.warning("Please input some text!");
        setLoading(false);
        return;
      }

      let data = {
        text: inputText,
        userId: userID,
      };

      const wordCompressionResponse = await checkCompression(data);

      if (wordCompressionResponse && wordCompressionResponse.data) {
        let outputText = "";

        if (Array.isArray(wordCompressionResponse.data.output)) {
          outputText = wordCompressionResponse.data.output.join("\n");
        } else if (typeof wordCompressionResponse.data.output === "string") {
          outputText = wordCompressionResponse.data.output;
        } else {
          outputText = JSON.stringify(wordCompressionResponse.data);
        }

        if (outputText.startsWith("\n")) {
          outputText = outputText.substring(1);
        }

        setOutputText(outputText);
        setIsParaphrase(true);

        toast.success("Content has been shortened.");
      } else {
        toast.error("No response received for compression");
      }
    } catch (error) {
      console.error("Error in checkWordsCompression:", error);
      toast.error("Failed to compress text. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const checkWordsExpansion = async () => {
    setLoading(true);
    try {
      if (!inputText.trim()) {
        toast.warning("Please input some text!");
        setLoading(false);
        return;
      }

      let data = {
        text: inputText,
        userId: userID,
      };

      const wordExpansionResponse = await checkExpansion(data);

      if (wordExpansionResponse && wordExpansionResponse.data) {
        let outputText = "";

        if (Array.isArray(wordExpansionResponse.data.output)) {
          outputText = wordExpansionResponse.data.output.join("\n");
        } else if (typeof wordExpansionResponse.data.output === "string") {
          outputText = wordExpansionResponse.data.output;
        } else {
          outputText = JSON.stringify(wordExpansionResponse.data);
        }

        if (outputText.startsWith("\n")) {
          outputText = outputText.substring(1);
        }

        setOutputText(outputText);
        setIsParaphrase(true);

        toast.success("The content has been enriched with more details.");
      } else {
        toast.error("No response received for expansion");
      }
    } catch (error) {
      console.error("Error in checkWordsExpansion:", error);
      toast.error("Failed to expand text. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [isFeatureInside, setIsFeatureInside] = useState(false);

  const handleFutureButton = async (type) => {
    if(accessToMoreOptions == false){
      setSubReqOpen(true)
      setIsFeatureInside(true)
      return
      
    }
    try {
      if(accessTocustom == false){
        setSubReqOpen(true)
        setIsFeatureInside(true)
        return

      }
      const checkailimit = await checkAIRequestLimit(userID);
      if (checkailimit === false) {
        toast.warn("AI request limit reached.");
        return;
      }

      if (!inputText.trim()) {
        toast.warning("Please input some text!");
        return;
      }

      const wordCount = inputText.split(/\s+/).length;
      if (wordCount > 1500) {
        toast.warn("Words limit exceeded (max 1500 words)");
        return;
      }

      if (type === "shorten") {
        await checkWordsCompression();
        setCustomText("Custom");
      } else if (type === "expand") {
        await checkWordsExpansion();
        setCustomText("Custom");
      } else if (type === "custom") {
        setIsCustomOpen(true);
      } else {
        await GrammerCheckForParaphrase(type);
        setCustomText("Custom");
      }
    } catch (error) {
      console.error("Error in handleFutureButton:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  const onPetAssistant = async () => {
    try {
      const checkailimit = await checkAIRequestLimit(userID);
      if (checkailimit === false) {
        toast.warn("AI request limit reached.");
        return;
      }

      if (!inputText.trim()) {
        toast.warning("Please input some text!");
        return;
      }

      if (inputText.trim().split(/\s+/).length > 1500) {
        toast.warn("Words limit exceeded");
        return;
      }

      await GrammerCheckForParaphrase("standard");
    } catch (error) {
      console.error("Error in onPetAssistant:", error);
      toast.error("An error occurred while processing your request.");
    }
  };

  const handleDelete1 = () => {
    setInputText("");
    setOutputText("");
    setIsParaphrase(false);
    setCharacters({ ...characters, now: 0 });
  };

  const handleDelete = () => {
    setOutputText("");
    setIsParaphrase(false);
  };

  const handleCopy = () => {
    if (!outputText.trim()) {
      toast.warn("No text to copy");
      return;
    }

    navigator.clipboard.writeText(outputText).then(
      () => {
        toast.success("Text copied to clipboard!");
      },
      (err) => {
        console.error("Could not copy text: ", err);
        toast.error("Failed to copy text");
      }
    );
  };

  const handleDownload = () => {
    if (!outputText.trim()) {
      toast.warn("No text to download");
      return;
    }

    const blob = new Blob([outputText], { type: "text/plain" });
    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = "paraphrased_text.txt";
    anchor.click();
    URL.revokeObjectURL(anchor.href);
    toast.success("File downloaded successfully!");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".txt")) {
      toast.warn("Only .txt files are supported");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const wordCount = text.split(/\s+/).length;

      if (wordCount > 1500) {
        toast.warn("File content exceeds 1500 word limit");
        return;
      }

      setInputText(text);
      toast.success("File uploaded successfully!");
    };

    reader.onerror = () => {
      toast.error("Failed to read file");
    };

    reader.readAsText(file);
  };

  const handleCustomSelection = (type) => {
    if (!inputText.trim()) {
      toast.warning("Please input some text!");
      return;
    }

    GrammerCheckForParaphrase(type);
    setCustomText(type);
    setIsCustomOpen(false);
    setCustomTextValue("");
  };

  const handleCustomClick = () => {
    if (!customTextValue.trim()) {
      toast.warning("Please enter a custom tone");
      return;
    }

    handleCustomSelection(customTextValue);
  };

  const handleSuggestionChange = (e) => {
    setCustomTextValue(e.target.value);
  };

  const handleClose = () => {
    setIsCustomOpen(false);
    setCustomTextValue("");
  };

  const handleToneClick = (toneId) => {
    if(accessToTone == false){
      setSubReqOpen(true)
      setIsFeatureInside(true)
      return

    }
    setSelectedTone(toneId);
    handleFutureButton(toneId);
  };

  const countWords = (text) => {
    return text.trim().length > 0 ? text.trim().split(/\s+/).length : 0;
  };

  const inputWordCount = countWords(inputText);
  const outputWordCount = countWords(outputText);

  const renderSelect = () => {
    if (!isClient) return null;

    return (
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <Select
          value={selectDropdownTone}
          onChange={(e) => {
            const selectedValue = e.target.value;
            if (selectedValue !== "select") {
              handleFutureButton(selectedValue);
              setSelectedTone(selectedValue);
              setSelectDropdownTone(selectedValue);
            }
          }}
          displayEmpty
          sx={{
            borderRadius: 2,
            height: 36,
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
                "& .MuiMenuItem-root": {
                  minHeight: 36,
                  display: "flex",
                  alignItems: "center",
                },
              },
            },
          }}
        >
          <MenuItem value="select">More Options</MenuItem>
          <MenuItem
            disabled={!accessToParaphrase || !inputText.trim()}
            value="shorten"
          >
            🔽 Shorten
          </MenuItem>
          <MenuItem
            disabled={!accessToParaphrase || !inputText.trim()}
            value="expand"
          >
            🔼 Expand
          </MenuItem>
        </Select>
      </FormControl>
    );
  };

  if (!isClient) {
    return (
      <Box
        sx={{
          height: "95vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e7ecf4ff 100%)",
          overflow: "hidden",
        }}
      >
        <Loader loading={true} />
      </Box>
    );
  }

  return (
    <>
    <SubscriptionRequired open={subReqOpen} setOpen={setSubReqOpen} isFeatureInside={isFeatureInside} setIsFeatureInside={setIsFeatureInside} path={"paraphrase"}/>
      <Box
         sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)",
        }}
      >
        <Loader loading={loading} />
        <Snackbar
          open={notification.open}
          autoHideDuration={3000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity="info"
            onClose={() => setNotification({ ...notification, open: false })}
          >
            {notification.message}
          </Alert>
        </Snackbar>

        <Dialog
          open={isCustomOpen}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tune color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Customize Your Tone
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Describe how you want your text to sound
              </Typography>
              <TextField
                fullWidth
                placeholder='E.g., "like a News Article"'
                value={customTextValue}
                onChange={handleSuggestionChange}
                variant="outlined"
                size="small"
              />
            </Box>

            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Quick Suggestions
            </Typography>

            <Grid container spacing={2}>
              {customSuggestions.map((item, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Chip
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <span>{item.icon}</span>
                        <span>{item.label}</span>
                      </Box>
                    }
                    onClick={() => handleCustomSelection(item.label)}
                    color="primary"
                    variant="outlined"
                    sx={{
                      width: "100%",
                      height: "50px",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 3,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleCustomClick}
              variant="contained"
              disabled={!customTextValue.trim()}
              startIcon={<AutoFixHigh />}
            >
              Apply Custom Tone
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ flexShrink: 0, p: 3, pb: 2 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            <CardContent sx={{ pb: 2, "&:last-child": { pb: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <AutoFixHigh sx={{ fontSize: 36, color: "primary.main" }} />
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        color: "primary.main",
                        lineHeight: 1.2,
                        mb: 0.5,
                      }}
                    >
                      AI Paraphraser
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      Transform your text with intelligent rephrasing powered by
                      AI
                    </Typography>
                  </Box>
                </Box>

                <Link to="/docTags" style={{ textDecoration: "none" }}>
                  <Button
                    variant="contained"
                    sx={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "#fff",
                      borderRadius: 3,
                      whiteSpace: "nowrap",
                      minWidth: "120px",
                      px: 3,
                      py: 1.2,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.95rem",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
                      },
                    }}
                  >
                    ← Back
                  </Button>
                </Link>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  alignItems: "center",
                  minHeight: 40,
                }}
              >
                {buttons.map((button) => (
                  <Chip
                    key={button.id}
                    icon={
                      <span style={{ fontSize: "16px" }}>{button.icon}</span>
                    }
                    label={button.label}
                    disabled={!inputText || !accessToParaphrase}
                    onClick={() => handleToneClick(button.id)}
                    color={selectedTone === button.id ? "primary" : "default"}
                    variant={selectedTone === button.id ? "filled" : "outlined"}
                    sx={{
                      height: "36px",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      transition: "all 0.3s",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: 2,
                      },
                    }}
                  />
                ))}

                <Box
                  sx={{
                    ml: "auto",
                    display: "flex",
                    gap: 1,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {renderSelect()}

                  <Button
                    variant="contained"
                    onClick={() => handleFutureButton("custom")}
                    disabled={!accessToParaphrase || !inputText.trim()}
                    startIcon={<Tune />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      height: 36,
                      minWidth: 100,
                    }}
                  >
                    {customText}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box
          sx={{ flexGrow: 1, px: 3, pb: 3, overflow: "hidden", minHeight: 0 }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              height: "500px",
            }}
          >
            <Card
              elevation={4}
              sx={{
                height: "100%",
                borderRadius: 3,
                background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap:2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  📝 Input Text
                </Typography>
                <Chip
                  label={`${inputWordCount} Words`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    overflow: "auto",
                    height: "350px",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f5f5f5",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c1c1c1",
                      borderRadius: "4px",
                      "&:hover": {
                        background: "#a8a8a8",
                      },
                    },
                  }}
                >
                  <TextField
                    multiline
                    fullWidth
                    value={inputText}
                    onChange={handleInputChange}
                    placeholder="✨ Start typing or paste your text here to paraphrase..."
                    variant="outlined"
                    InputProps={{
                      sx: {
                        fontSize: "15px",
                        lineHeight: 1.6,
                        minHeight: "320px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        minHeight: "320px",
                        alignItems: "flex-start",
                      },
                      "& textarea": {
                        overflow: "auto !important",
                        minHeight: "300px !important",
                        resize: "none",
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "black.50",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Circle
                    sx={{
                      fontSize: 10,
                      color:
                        inputWordCount > 1400 ? "error.main" : "success.main",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    {inputWordCount} / 1500 Words
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    disabled={!accessToUpload}
                    startIcon={<CloudUpload />}
                    sx={{ borderRadius: 2, textTransform: "none" }}
                  >
                    Upload
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".txt"
                      hidden
                      onChange={handleFileUpload}
                    />
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    onClick={onPetAssistant}
                    disabled={!inputText || !accessToParaphrase}
                    startIcon={<AutoFixHigh />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    {!isParaphrase ? "Paraphrase" : "Re-Phrase"}
                  </Button>

                  <IconButton
                    size="small"
                    onClick={handleDelete1}
                    disabled={!accessToDelete}
                    sx={{
                      color: accessToDelete ? "error.main" : "black.500",
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            </Card>

            <Card
              elevation={4}
              sx={{
                height: "100%",
                borderRadius: 3,
                background: "linear-gradient(145deg, #ffffff 0%, #e8f5e9 100%)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  bgcolor: "success.main",
                  color: "white",
                  p: 1.5,
                  borderRadius: "12px 12px 0 0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  ✨ Paraphrased Result
                </Typography>
                <Chip
                  label={`${outputWordCount} Words`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    overflow: "auto",
                    maxHeight: "100%",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f5f5f5",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c1c1c1",
                      borderRadius: "4px",
                      "&:hover": {
                        background: "#a8a8a8",
                      },
                    },
                  }}
                >
                  <TextField
                    multiline
                    fullWidth
                    value={outputText}
                    placeholder="Your paraphrased text will appear here..."
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontSize: "15px",
                        lineHeight: 1.6,
                        minHeight: "300px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        minHeight: "300px",
                        alignItems: "flex-start",
                      },
                      "& textarea": {
                        overflow: "auto !important",
                        minHeight: "300px !important",
                        resize: "none",
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  p: 2,
                  bgcolor: "black.50",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Circle sx={{ fontSize: 10, color: "success.main" }} />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    {outputWordCount} Words Generated
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={handleDownload}
                    disabled={!outputWordCount}
                    sx={{
                      color: outputWordCount ? "success.main" : "black.500",
                    }}
                  >
                    <Download />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    disabled={!outputWordCount}
                    sx={{
                      color: outputWordCount ? "info.main" : "black.500",
                    }}
                  >
                    <ContentCopy />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={handleDelete}
                    disabled={!accessToDelete || !outputWordCount}
                    sx={{
                      color:
                        accessToDelete && outputWordCount
                          ? "error.main"
                          : "black.500",
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            </Card>
          </Box>
        </Box>
      </Box>
    </>
  );
}

const mapStateToProps = (state) => ({
  currentUser: state.user.currentUser,
  features: state.features.features,
});

export default connect(mapStateToProps)(NewParaphrase);
